import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import './SignUp.scss'
import MainURL from '../MainURL'

const TOAST_DURATION_MS = 2400
const MIN_PASSWORD_LENGTH = 8
const DEFAULT_SIGNUP_ERROR_MESSAGE = '회원가입에 실패했습니다. 잠시 후 다시 시도해 주세요.'
const SIGNUP_REDIRECT_DELAY_MS = 1600

function SignUp() {
  const navigate = useNavigate()
  const [signupName, setSignupName] = useState('')
  const [signupEmail, setSignupEmail] = useState('')
  const [signupPassword, setSignupPassword] = useState('')
  const [signupPasswordConfirm, setSignupPasswordConfirm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [isToastVisible, setIsToastVisible] = useState(false)
  const toastTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current)
      }
    }
  }, [])

  const showToast = (message: string) => {
    if (toastTimerRef.current) {
      window.clearTimeout(toastTimerRef.current)
    }
    setToastMessage(message)
    setIsToastVisible(true)
    toastTimerRef.current = window.setTimeout(() => setIsToastVisible(false), TOAST_DURATION_MS)
  }

  const handleSignUpSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const name = signupName.trim()
    const email = signupEmail.trim()
    if (!name || !email || !signupPassword || !signupPasswordConfirm || isSubmitting) {
      return
    }

    if (signupPassword.length < MIN_PASSWORD_LENGTH) {
      showToast(`비밀번호는 ${MIN_PASSWORD_LENGTH}자 이상 입력해 주세요.`)
      return
    }

    if (signupPassword !== signupPasswordConfirm) {
      showToast('비밀번호가 일치하지 않습니다.')
      return
    }

    setIsSubmitting(true)
    try {
      const response = await fetch(`${MainURL}/admin/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: signupPassword, name }),
      })

      const data = await response.json().catch(() => null)

      if (!response.ok) {
        showToast(data?.message || DEFAULT_SIGNUP_ERROR_MESSAGE)
        return
      }

      showToast(data?.message || '가입 신청이 완료되었습니다. 관리자 승인 후 로그인할 수 있습니다.')
      window.setTimeout(() => navigate('/admin'), SIGNUP_REDIRECT_DELAY_MS)
    } catch (error) {
      showToast(DEFAULT_SIGNUP_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='SignUp'>
      <section className='signup-screen' aria-labelledby='signupTitle'>
        <form className='signup-card' onSubmit={handleSignUpSubmit}>
          <div className='signup-brand' aria-hidden='true'>DT</div>
          <div className='signup-heading'>
            <p>드럼으로 만드는 나만의 음악 이야기</p>
            <h1 id='signupTitle'>회원가입</h1>
          </div>
          <label className='signup-field'>
            이름
            <input
              type='text'
              value={signupName}
              onChange={(event) => setSignupName(event.target.value)}
              autoComplete='name'
              required
            />
          </label>
          <label className='signup-field'>
            이메일
            <input
              type='email'
              value={signupEmail}
              onChange={(event) => setSignupEmail(event.target.value)}
              autoComplete='email'
              required
            />
          </label>
          <label className='signup-field'>
            비밀번호
            <input
              type='password'
              value={signupPassword}
              onChange={(event) => setSignupPassword(event.target.value)}
              autoComplete='new-password'
              required
            />
          </label>
          <label className='signup-field'>
            비밀번호 확인
            <input
              type='password'
              value={signupPasswordConfirm}
              onChange={(event) => setSignupPasswordConfirm(event.target.value)}
              autoComplete='new-password'
              required
            />
          </label>
          <button className='primary-button signup-submit' type='submit' disabled={isSubmitting}>
            {isSubmitting ? '가입 신청 중...' : '회원가입'}
          </button>
          <button className='signup-back' type='button' onClick={() => navigate('/admin')}>로그인으로 돌아가기</button>
          <p className='signup-copyright'>Copyright © JUEUNEDUTECH. All rights reserved.</p>
        </form>
      </section>

      <div className={`toast${isToastVisible ? ' show' : ''}`} role='status' aria-live='polite'>
        {toastMessage}
      </div>
    </div>
  )
}

export default SignUp
